import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { track, PLAN_VALUE } from "@/lib/analytics";
import { DonkeyMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [reportId, setReportId] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setReportId(data.report_id);
        if (data.payment_status === "paid") {
          clearInterval(pollRef.current);
          setStatus("paid");
          const key = `hd_purchase_${sessionId}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            track("purchase", { plan: data.plan, value: PLAN_VALUE[data.plan] });
          }
        }
        else if (["failed", "expired"].includes(data.payment_status)) { clearInterval(pollRef.current); setStatus("error"); }
      } catch { /* keep polling */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    const timeout = setTimeout(() => { clearInterval(pollRef.current); setStatus((s) => s === "checking" ? "slow" : s); }, 20000);
    return () => { clearInterval(pollRef.current); clearTimeout(timeout); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-donkey-bg grain flex flex-col items-center justify-center px-5 text-center">
      {status === "paid" ? (
        <>
          <CheckCircle2 className="w-16 h-16 text-donkey-green mb-6" />
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-donkey-warm">Payment confirmed.</h1>
          <p className="mt-3 text-donkey-grey max-w-md">Your Prove subscription is active. Your full Donkey's Verdict is unlocked, and your first month of validation is ready.</p>
          <Button onClick={() => navigate(reportId ? `/report/${reportId}` : "/dashboard")}
            className="mt-8 bg-donkey-orange text-donkey-black font-semibold h-12 px-7" data-testid="view-report-btn">
            View the full verdict <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </>
      ) : status === "error" ? (
        <>
          <h1 className="font-heading font-bold text-2xl text-donkey-warm">Something went sideways.</h1>
          <p className="mt-3 text-donkey-grey max-w-md">We couldn't confirm the payment. If you were charged, it will resolve shortly — nothing is lost.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-8 bg-donkey-orange text-donkey-black font-semibold">Back to dashboard</Button>
        </>
      ) : (
        <>
          <DonkeyMark size={64} className="animate-pulse-soft mb-6" />
          <h1 className="font-heading font-bold text-xl text-donkey-warm">Confirming your payment…</h1>
          <p className="mt-2 text-donkey-grey text-sm">{status === "slow" ? "Taking a little longer than usual — hang tight." : "One moment."}</p>
          <Loader2 className="w-6 h-6 animate-spin text-donkey-orange mt-6" />
        </>
      )}
    </div>
  );
}
